import { useState, useCallback, useEffect } from "react";
import { supabase, isSupabaseConnected } from "@/lib/supabase";

export interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  insurance_type: string;
  source: string;
  status: "new" | "contacted" | "qualified" | "converted" | "lost";
  created_at: string;
  assigned_to?: string;
  notes?: string;
}

export interface Appointment {
  id: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  insurance_type: string;
  preferred_date: string;
  preferred_time: string;
  source?: string;
  status: "scheduled" | "confirmed" | "completed" | "cancelled" | "no_show";
  created_at: string;
  notes?: string;
}

export interface AssignmentRule {
  id: string;
  insuranceTypes: string[];
  advisorName: string;
  advisorEmail?: string;
  advisorPhone?: string;
  active: boolean;
}

export const defaultAssignmentRules: AssignmentRule[] = [
  { id: "rule-1", insuranceTypes: ["Health Insurance", "Group Health Insurance"], advisorName: "Amit Patel", advisorEmail: "amit.patel@expertadvise.in", advisorPhone: "+91 98765 11111", active: true },
  { id: "rule-2", insuranceTypes: ["Term Insurance", "Life Insurance"], advisorName: "Rahul Mehta", advisorEmail: "rahul.mehta@expertadvise.in", advisorPhone: "+91 98765 22222", active: true },
  { id: "rule-3", insuranceTypes: ["Car Insurance", "Marine Insurance", "Travel Insurance"], advisorName: "Priya Sharma", advisorEmail: "priya.sharma@expertadvise.in", advisorPhone: "+91 98765 33333", active: true },
  { id: "rule-4", insuranceTypes: ["SIP Investments", "Mutual Funds", "Retirement Planning", "Financial Planning"], advisorName: "Kavita Joshi", advisorEmail: "kavita.joshi@expertadvise.in", advisorPhone: "+91 98765 44444", active: true },
  { id: "rule-5", insuranceTypes: ["Corporate Insurance"], advisorName: "Deepak Sinha", advisorEmail: "deepak.sinha@expertadvise.in", advisorPhone: "+91 98765 55555", active: true },
];

export function getAutoAssignAdvisor(insuranceType: string): string | null {
  const rules = JSON.parse(localStorage.getItem("expertadvise_assignment_rules") || "null") || defaultAssignmentRules;
  for (const rule of rules) {
    if (rule.active && rule.insuranceTypes.includes(insuranceType)) {
      return rule.advisorName;
    }
  }
  return null;
}

export function getSourceAnalytics(leads: Lead[]) {
  const sourceMap: Record<string, { count: number; converted: number; new: number }> = {};
  for (const lead of leads) {
    const src = lead.source || "Unknown";
    if (!sourceMap[src]) sourceMap[src] = { count: 0, converted: 0, new: 0 };
    sourceMap[src].count++;
    if (lead.status === "converted") sourceMap[src].converted++;
    if (lead.status === "new") sourceMap[src].new++;
  }
  return Object.entries(sourceMap)
    .map(([source, data]) => ({ source, ...data }))
    .sort((a, b) => b.count - a.count);
}

export function getInsuranceTypeAnalytics(leads: Lead[]) {
  const typeMap: Record<string, { count: number; converted: number }> = {};
  for (const lead of leads) {
    const t = lead.insurance_type || "Unknown";
    if (!typeMap[t]) typeMap[t] = { count: 0, converted: 0 };
    typeMap[t].count++;
    if (lead.status === "converted") typeMap[t].converted++;
  }
  return Object.entries(typeMap)
    .map(([type, data]) => ({ type, ...data }))
    .sort((a, b) => b.count - a.count);
}

export function getTodaysAppointments(appointments: Appointment[]): Appointment[] {
  const today = new Date().toISOString().split("T")[0];
  return appointments
    .filter((a) => a.preferred_date === today && ["scheduled", "confirmed"].includes(a.status))
    .sort((a, b) => a.preferred_time.localeCompare(b.preferred_time));
}

export function useAdminData() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [realDataLoaded, setRealDataLoaded] = useState(false);
  const [newLeadIds, setNewLeadIds] = useState<string[]>([]);
  const [newAppointmentIds, setNewAppointmentIds] = useState<string[]>([]);
  const [realtimeConnected, setRealtimeConnected] = useState(false);

  const isRealBackend = isSupabaseConnected && supabase;

  const fetchLeads = useCallback(async () => {
    if (!isRealBackend) return;
    setLoading(true);
    setError(null);
    const { data, error: sbError } = await supabase
      .from("leads")
      .select("*")
      .order("created_at", { ascending: false });
    setLoading(false);
    if (sbError) {
      setError(sbError.message);
      return;
    }
    setRealDataLoaded(true);
    setLeads((data as Lead[]) || []);
  }, [isRealBackend]);

  const fetchAppointments = useCallback(async () => {
    if (!isRealBackend) return;
    setLoading(true);
    setError(null);
    const { data, error: sbError } = await supabase
      .from("appointments")
      .select("*")
      .order("created_at", { ascending: false });
    setLoading(false);
    if (sbError) {
      setError(sbError.message);
      return;
    }
    setRealDataLoaded(true);
    setAppointments((data as Appointment[]) || []);
  }, [isRealBackend]);

  // Realtime subscriptions
  useEffect(() => {
    if (!isRealBackend) return;

    fetchLeads();
    fetchAppointments();

    const leadsChannel = supabase
      .channel("leads-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "leads" },
        (payload) => {
          if (payload.eventType === "INSERT") {
            const newLead = payload.new as Lead;
            setNewLeadIds((prev) => [...prev.slice(-4), newLead.id]);
            setLeads((prev) => {
              // avoid duplicates
              if (prev.some((l) => l.id === newLead.id)) return prev;
              return [newLead, ...prev];
            });
          } else if (payload.eventType === "UPDATE") {
            setLeads((prev) => prev.map((l) => (l.id === (payload.new as Lead).id ? (payload.new as Lead) : l)));
          } else if (payload.eventType === "DELETE") {
            setLeads((prev) => prev.filter((l) => l.id !== (payload.old as Lead).id));
          }
        }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") setRealtimeConnected(true);
        if (status === "CLOSED" || status === "CHANNEL_ERROR") setRealtimeConnected(false);
      });

    const appointmentsChannel = supabase
      .channel("appointments-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "appointments" },
        (payload) => {
          if (payload.eventType === "INSERT") {
            const newAppt = payload.new as Appointment;
            setNewAppointmentIds((prev) => [...prev.slice(-4), newAppt.id]);
            setAppointments((prev) => {
              if (prev.some((a) => a.id === newAppt.id)) return prev;
              return [newAppt, ...prev];
            });
          } else if (payload.eventType === "UPDATE") {
            setAppointments((prev) => prev.map((a) => (a.id === (payload.new as Appointment).id ? (payload.new as Appointment) : a)));
          } else if (payload.eventType === "DELETE") {
            setAppointments((prev) => prev.filter((a) => a.id !== (payload.old as Appointment).id));
          }
        }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") setRealtimeConnected(true);
        if (status === "CLOSED" || status === "CHANNEL_ERROR") setRealtimeConnected(false);
      });

    return () => {
      leadsChannel.unsubscribe();
      appointmentsChannel.unsubscribe();
    };
  }, [isRealBackend, fetchLeads, fetchAppointments]);

  // Clear new lead highlights after 8 seconds
  useEffect(() => {
    if (newLeadIds.length === 0) return;
    const timer = setTimeout(() => setNewLeadIds([]), 8000);
    return () => clearTimeout(timer);
  }, [newLeadIds]);

  useEffect(() => {
    if (newAppointmentIds.length === 0) return;
    const timer = setTimeout(() => setNewAppointmentIds([]), 8000);
    return () => clearTimeout(timer);
  }, [newAppointmentIds]);

  const updateLeadStatus = useCallback(async (id: string, status: Lead["status"]) => {
    if (isRealBackend) {
      const { error: sbError } = await supabase
        .from("leads")
        .update({ status })
        .eq("id", id);
      if (sbError) {
        setError(sbError.message);
        return;
      }
    }
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
  }, [isRealBackend]);

  const updateAppointmentStatus = useCallback(async (id: string, status: Appointment["status"]) => {
    if (isRealBackend) {
      const { error: sbError } = await supabase
        .from("appointments")
        .update({ status })
        .eq("id", id);
      if (sbError) {
        setError(sbError.message);
        return;
      }
    }
    setAppointments((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
  }, [isRealBackend]);

  const assignLead = useCallback(async (id: string, advisorName: string | null) => {
    if (isRealBackend) {
      const { error: sbError } = await supabase
        .from("leads")
        .update({ assigned_to: advisorName })
        .eq("id", id);
      if (sbError) {
        setError(sbError.message);
        return;
      }
    }
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, assigned_to: advisorName || undefined } : l)));
  }, [isRealBackend]);

  const autoAssignLead = useCallback(async (id: string, insuranceType: string) => {
    const advisor = getAutoAssignAdvisor(insuranceType);
    if (advisor) {
      await assignLead(id, advisor);
    }
  }, [assignLead]);

  const dismissNewLead = useCallback((id: string) => {
    setNewLeadIds((prev) => prev.filter((lid) => lid !== id));
  }, []);

  const dismissNewAppointment = useCallback((id: string) => {
    setNewAppointmentIds((prev) => prev.filter((aid) => aid !== id));
  }, []);

  const stats = {
    totalLeads: leads.length,
    newLeads: leads.filter((l) => l.status === "new").length,
    convertedLeads: leads.filter((l) => l.status === "converted").length,
    totalAppointments: appointments.length,
    scheduledToday: appointments.filter((a) => a.preferred_date === new Date().toISOString().split("T")[0] && a.status === "scheduled").length,
    completedAppointments: appointments.filter((a) => a.status === "completed").length,
    conversionRate: leads.length > 0 ? Math.round((leads.filter((l) => l.status === "converted").length / leads.length) * 100) : 0,
  };

  return {
    leads,
    appointments,
    stats,
    loading,
    error,
    realDataLoaded,
    realtimeConnected,
    newLeadIds,
    newAppointmentIds,
    updateLeadStatus,
    updateAppointmentStatus,
    assignLead,
    autoAssignLead,
    dismissNewLead,
    dismissNewAppointment,
    refetchLeads: fetchLeads,
    refetchAppointments: fetchAppointments,
  };
}